import axios from 'axios'
import searchService from '../services/search'

// Module-scoped so a keystroke can cancel the request the previous keystroke started,
// and a late response from an old keystroke can never overwrite a newer one
// (guardrail: never show stale results while a newer request is in flight).
let inFlight = null
let requestSeq = 0

const MIN_CHARS = 2

const emptyResults = () => ({ brands: [], categories: [], products: [], productsTotal: 0 })

export const state = {
  query: '',
  brands: [],
  categories: [],
  products: [],
  productsTotal: 0,
  loading: false,
  // A completed request exists for the current (>= MIN_CHARS) query — lets the UI tell
  // "typing / in flight" apart from "searched, nothing found".
  hasSearched: false
}

export const getters = {
  searchQuery: (state) => state.query,
  searchLoading: (state) => state.loading,
  searchBrands: (state) => state.brands,
  searchCategories: (state) => state.categories,
  searchProducts: (state) => state.products,
  searchProductsTotal: (state) => state.productsTotal,
  searchHasResults: (state) =>
    state.brands.length > 0 || state.categories.length > 0 || state.products.length > 0,
  searchHasSearched: (state) => state.hasSearched
}

export const mutations = {
  SET_SEARCH_QUERY (state, q) {
    state.query = q
  },
  SET_SEARCH_LOADING (state, value) {
    state.loading = value
  },
  SET_SEARCH_RESULTS (state, { brands, categories, products, productsTotal }) {
    state.brands = brands
    state.categories = categories
    state.products = products
    state.productsTotal = productsTotal
    state.hasSearched = true
  },
  RESET_SEARCH (state) {
    const empty = emptyResults()
    state.brands = empty.brands
    state.categories = empty.categories
    state.products = empty.products
    state.productsTotal = empty.productsTotal
    state.loading = false
    state.hasSearched = false
  }
}

export const actions = {
  // Field emptied / below threshold: cancel anything in flight and clear results.
  resetSearch ({ commit }) {
    if (inFlight) {
      inFlight.cancel()
      inFlight = null
    }
    requestSeq++
    commit('SET_SEARCH_QUERY', '')
    commit('RESET_SEARCH')
  },

  async runSearch ({ commit }, rawQuery) {
    const term = String(rawQuery || '').trim().replace(/\s+/g, ' ')
    commit('SET_SEARCH_QUERY', term)

    if (term.length < MIN_CHARS) {
      if (inFlight) {
        inFlight.cancel()
        inFlight = null
      }
      requestSeq++
      commit('RESET_SEARCH')
      return
    }

    // Supersede the previous request; only the newest may commit.
    if (inFlight) {
      inFlight.cancel()
    }
    inFlight = axios.CancelToken.source()
    const seq = ++requestSeq

    commit('SET_SEARCH_LOADING', true)
    try {
      const { data } = await searchService.search(term, inFlight.token)
      if (seq !== requestSeq) {
        return
      }
      commit('SET_SEARCH_RESULTS', {
        brands: data.brands || [],
        categories: data.categories || [],
        products: data.products || [],
        productsTotal: data.products_total || 0
      })
    } catch (error) {
      if (axios.isCancel(error) || seq !== requestSeq) {
        return
      }
      // Degrade quietly to an empty result set — /api/search is on the interceptor's
      // silent list, so no scary toast for a header typeahead hiccup.
      commit('SET_SEARCH_RESULTS', emptyResults())
    } finally {
      if (seq === requestSeq) {
        commit('SET_SEARCH_LOADING', false)
      }
    }
  }
}
